import type Context from '@/context';
import type { IChipModel, IChipInstanceModel, ICircuitModel } from '@/interfaces/models';
import { TileInstance } from '@/models/tiles/TileInstance';
import { Point } from '@/models/Point';
import { isValidTilePosition, toIndex } from '@/models/TileManagerModel';
import { composeRotation, rotatePointAroundOrigin } from '@/models/data/Rotation';
import { deepClone } from '@/utils/clone';
import { CircuitType } from '@/schemas/common';
import type { InterfaceTileState } from '@/schemas/tiles';
import { isInterfaceTile } from '@/models/tiles/TileRegistry';
import { connectChipBoundaryTiles } from '@/actions/chipActions';

type Plan = { owner: IChipModel; circuit: ICircuitModel; instance: IChipInstanceModel; oldTiles: TileInstance[]; tiles: Map<string, TileInstance> };

/** Validate every footprint before changing a definition or any placed instance. */
export function planChipReplacement(context: Context, source: IChipModel, draft: IChipModel, owners: IChipModel[]): Plan[] {
  const plans: Plan[] = [];
  const signature = (chip: IChipModel) => JSON.stringify({
    origin: chip.structureCircuit.originLocation,
    tiles: chip.structureCircuit.circuitModel.tiles.map(tile => {
      const state = deepClone(tile.state) as Record<string, any>;
      if (state?.connectedInterface) delete state.connectedInterface.chipInstanceId;
      return [tile.id, tile.tileType, tile.location, tile.meta.rotation, state, tile.effectiveIoSides];
    }),
    connections: chip.structureCircuit.circuitModel.connectionModel.dto,
  });
  if (signature(source) === signature(draft)) return plans;
  const origin = Point.fromData(draft.structureCircuit.originLocation);
  const structure = draft.structureCircuit.circuitModel.tiles;
  for (const owner of owners) {
    const circuit = owner.logicCircuit.circuitModel;
    const instances = circuit.chips.filter(instance => instance.chipId === source.id);
    if (!instances.length) continue;
    if (!structure.length) throw new Error('Placed chips need a structure. Add a structure tile or save a new copy.');
    const replacing = new Set<string>(instances.map(instance => instance.id));
    const occupied = new Set(circuit.tiles.filter(tile => !tile.meta.partOfChip || !replacing.has(tile.meta.partOfChip)).map(tile => toIndex(tile.location.x, tile.location.y)));
    const tilesByInstance = new Map<string, TileInstance[]>();
    for (const tile of circuit.tiles) if (tile.meta.partOfChip && replacing.has(tile.meta.partOfChip)) {
      const group = tilesByInstance.get(tile.meta.partOfChip) ?? [];
      group.push(tile); tilesByInstance.set(tile.meta.partOfChip, group);
    }
    for (const instance of instances) {
      const oldTiles = tilesByInstance.get(instance.id) ?? [];
      const oldByPosition = new Map(oldTiles.map(tile => [toIndex(tile.location.x, tile.location.y), tile]));
      const tiles = new Map<string, TileInstance>();
      const offset = Point.fromData(instance.originPoint).sub(origin);
      for (const template of structure) {
        const location = rotatePointAroundOrigin(Point.fromData(template.location), origin, instance.rotation).add(offset);
        if (!isValidTilePosition(location.x, location.y)) throw new Error(`The new structure extends beyond the map in ${owner.name}. Save a new copy or adjust its structure.`);
        const key = toIndex(location.x, location.y);
        if (occupied.has(key)) throw new Error(`The new structure overlaps a tile in ${owner.name}. Move that tile, adjust the structure, or save a new copy.`);
        occupied.add(key);
        const tile = new TileInstance(context, template.tileType, { chipInstanceId: owner.editingInstance.id, location, circuitType: CircuitType.ChipLogic });
        tile.meta = deepClone(template.meta);
        tile.meta.partOfChip = instance.id;
        tile.meta.rotation = composeRotation(template.meta.rotation, instance.rotation);
        tile.state = deepClone(template.state);
        tile.effectiveIoSides = template.effectiveIoSides && deepClone(template.effectiveIoSides);
        const previous = oldByPosition.get(key);
        if (previous?.tileType === tile.tileType) tile.id = previous.id;
        if (isInterfaceTile(tile)) {
          const state = tile.state as InterfaceTileState<unknown>;
          state.connectedInterface.chipInstanceId = instance.id;
          state.connectedInterface.circuitType = CircuitType.ChipLogic;
        }
        tiles.set(template.id, tile);
      }
      plans.push({ owner, circuit, instance, oldTiles, tiles });
    }
  }
  return plans;
}

export function applyChipReplacement(draft: IChipModel, plans: Plan[]) {
  const connections = draft.structureCircuit.circuitModel.connectionModel.dto.connections;
  const blocked = new Map([...new Set(plans.map(plan => plan.circuit))].map(circuit => [circuit, circuit.connectionModel.dto.disconnected ?? []]));
  // Remove all old footprints first, so adjoining replacements cannot see stale neighbors.
  for (const { circuit, oldTiles } of plans) for (const tile of oldTiles) {
    circuit.connectionModel.removeConnectionsForTile(tile.id);
    circuit.tileManagerModel.remove(tile);
  }
  for (const { circuit, tiles } of plans) circuit.tileManagerModel.addAll([...tiles.values()]);
  for (const { circuit, tiles } of plans) {
    for (const connection of connections) {
      const a = tiles.get(connection.a.tileId), b = tiles.get(connection.b.tileId);
      if (a && b) circuit.connectionModel.connect({ ...connection.a, tileId: a.id }, { ...connection.b, tileId: b.id });
    }
    connectChipBoundaryTiles([...tiles.values()], circuit.tileManagerModel, circuit.connectionModel);
  }
  for (const [circuit, edges] of blocked) {
    const ids = new Set(circuit.tiles.map(tile => tile.id));
    for (const edge of edges) if (ids.has(edge.a.tileId) && ids.has(edge.b.tileId)) circuit.connectionModel.disconnect(edge.a, edge.b, { preventReconnect: true });
  }
}


/** Appearance-only changes preserve footprint objects and existing placement history. */
export function applyChipTintUpdates(source: IChipModel, draft: IChipModel, owners: IChipModel[]) {
  const original = new Map(source.structureCircuit.circuitModel.tiles.map(tile => [tile.id, tile.meta.tint]));
  const changed = draft.structureCircuit.circuitModel.tiles.filter(tile => original.get(tile.id) !== tile.meta.tint);
  if (!changed.length) return;
  const origin = Point.fromData(draft.structureCircuit.originLocation);
  for (const owner of owners) {
    const circuit = owner.logicCircuit.circuitModel;
    for (const instance of circuit.chips) {
      if (instance.chipId !== source.id) continue;
      const offset = Point.fromData(instance.originPoint).sub(origin);
      for (const template of changed) {
        const position = rotatePointAroundOrigin(Point.fromData(template.location), origin, instance.rotation).add(offset);
        const tile = circuit.tileManagerModel.get(position);
        if (tile?.meta.partOfChip !== instance.id) continue;
        if (template.meta.tint) tile.meta.tint = template.meta.tint;
        else delete tile.meta.tint;
      }
    }
  }
}
